'use client'

import { useEffect, useCallback, useState, useRef } from 'react'

import Slider from 'rc-slider'
import { useIntl } from 'react-intl'
import { NumericFormat } from 'react-number-format'
import { styled } from 'styled-components'
import 'rc-slider/assets/index.css'

import { InputBordered } from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows, Typography } from '@b-stock/fe-core/design-system'

import { useTrackFilterUsage } from '../analytics'
import WithDynamicButton from '../WithDynamicApplyButton'

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
`

const StyledRange = styled(Slider)<{ $reverseTrack?: boolean }>`
  height: 1.25rem;
  padding: 0.5rem 0;

  .rc-slider-rail {
    background: ${({ $reverseTrack }) =>
      $reverseTrack
        ? Colors.Semantic.BStock.onhover
        : Colors.Semantic.Neutral.medium_grey};
    height: 0.25rem;
    margin-bottom: -0.25rem;
    border-radius: 6.25rem;
  }

  .rc-slider-track {
    background: ${({ $reverseTrack }) =>
      $reverseTrack
        ? Colors.Semantic.Neutral.medium_grey
        : Colors.Semantic.BStock.onhover};
    height: 0.25rem;
    margin-bottom: -0.25rem;
    border-radius: 6.25rem;
  }

  .rc-slider-handle {
    background: ${Colors.Semantic.Neutral.white};
    border: 0.0625rem solid ${Colors.Semantic.BStock.default.lightBackground};
    ${Shadows.Popovers}
    height: 1.25rem;
    width: 1.25rem;
    opacity: 1;
    margin-top: -0.5rem;
  }

  &&& {
    .rc-slider-handle-dragging {
      background: ${Colors.Semantic.BStock.onclick.lightBackground};
      border-color: ${Colors.Semantic.BStock.onclick.lightBackground};
      ${Shadows.Popovers}
    }
  }
`

const MinMaxContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`

const MinMaxLabel = styled(NumericFormat)`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.black};
`

const PercentageStyledInputBordered = styled(InputBordered)`
  input {
    flex: 0 !important;
    width: 5rem !important;
  }
`

export const minPercentage = 0
export const maxPercentage = 100
export const defaultPercentage = 100

const PercentageRangeFilter = ({
  selected,
  onChange,
  desktopScreen = true,
  analyticsTitle,
  defaultPercentage: defaultPct = defaultPercentage,
  inputLabel = 'Filters.msrpPercentage.label',
  sliderStep = 0.01,
  buttonName = 'filters',
  onApplied,
  reverseTrack = false,
}: {
  selected: number | null
  onChange: (value: number) => void
  desktopScreen?: boolean
  onError?: (value: boolean) => void
  analyticsTitle: string
  defaultPercentage?: number
  inputLabel?: string
  sliderStep?: number
  buttonName?: string
  onApplied?: (value: number, buttonType: 'slider' | 'top_range') => void
  reverseTrack?: boolean
}) => {
  const intl = useIntl()
  const trackFilterUsage = useTrackFilterUsage<{
    filter_title: string
    percentage: number | null
    button_type?: string
    top_range?: number
    button_range?: number
  }>(buttonName)

  const [percentage, setPercentage] = useState<number | null>(
    selected ?? defaultPct
  )
  const [showResults, setShowResults] = useState(false)

  const sliderRef = useRef<HTMLDivElement>(null)
  const isSliderDragging = useRef(false)

  useEffect(() => {
    setPercentage(selected ?? defaultPct) // oxlint-disable-line react/set-state-in-effect
    isSliderDragging.current = false
    setShowResults(false)
  }, [selected, defaultPct])

  const handleSliderChange = useCallback((value: number | number[]) => {
    isSliderDragging.current = true
    const singleValue = Array.isArray(value) ? (value[0] ?? null) : value
    setPercentage(singleValue)
  }, [])

  const handleInputChange = useCallback(
    (value: string) => {
      if (value === '') {
        setPercentage(null)
        return
      }

      const parsedValue = parseFloat(value)
      if (isNaN(parsedValue)) {
        return
      }

      const validValue = Math.min(
        Math.max(parsedValue, minPercentage),
        maxPercentage
      )
      setPercentage(validValue)

      if (!desktopScreen) {
        onChange(validValue)
      }
      if (!isSliderDragging.current) {
        setShowResults(true)
      }
    },
    [desktopScreen, onChange]
  )

  const handleBlur = useCallback(() => {
    if (
      percentage === null ||
      percentage > maxPercentage ||
      percentage < minPercentage
    ) {
      setPercentage(defaultPct)
      setShowResults(false)
      onChange(defaultPct)
    }
  }, [percentage, onChange, defaultPct])

  const handleSliderComplete = useCallback(() => {
    const buttonType = isSliderDragging.current ? 'slider' : 'top_range'
    isSliderDragging.current = false
    const appliedValue = percentage ?? maxPercentage
    trackFilterUsage({
      filter_title: analyticsTitle,
      percentage: appliedValue,
      button_type: buttonType,
      top_range: Math.round(appliedValue),
      button_range: 0,
    })
    onApplied?.(appliedValue, buttonType)

    onChange(appliedValue)
    setShowResults(false)
  }, [percentage, trackFilterUsage, analyticsTitle, onChange, onApplied])

  const handleShowResults = useCallback(() => {
    handleSliderComplete()
  }, [handleSliderComplete])

  return (
    <WithDynamicButton
      buttonContent={intl.formatMessage({
        id: 'Filters.rangeDistance.filterButton',
      })}
      isButtonVisible={desktopScreen && showResults}
      onClick={handleShowResults}
    >
      <div>
        <RangeContainer data-testid="percentage-slider" ref={sliderRef}>
          <StyledRange
            min={minPercentage}
            max={maxPercentage}
            step={sliderStep}
            value={percentage ?? 0}
            onChange={handleSliderChange}
            onChangeComplete={handleSliderComplete}
            $reverseTrack={reverseTrack}
          />
        </RangeContainer>
        <MinMaxContainer>
          <MinMaxLabel
            displayType="text"
            value={minPercentage}
            suffix="%"
            data-testid="min"
          />
          <MinMaxLabel
            data-testid="max"
            displayType="text"
            value={maxPercentage}
            suffix="%"
          />
        </MinMaxContainer>
        <NumericFormat
          customInput={PercentageStyledInputBordered}
          inputMode="decimal"
          label={intl.formatMessage({ id: inputLabel })}
          value={percentage ?? ''}
          onValueChange={(values, sourceInfo) => {
            if ((sourceInfo.source as string) !== 'prop') {
              const { value } = values
              handleInputChange(value)
            }
          }}
          onBlur={handleBlur}
          suffix="%"
          decimalScale={2}
          fixedDecimalScale={false}
          allowNegative={false}
          isAllowed={(values) => {
            const { floatValue } = values
            return (
              floatValue === undefined || (floatValue >= 0 && floatValue <= 100)
            )
          }}
          className="percentage-input"
          aria-label="Enter percentage"
          data-testid="percentage-input"
        />
      </div>
    </WithDynamicButton>
  )
}

export default PercentageRangeFilter
